import React, { useState, useRef, useEffect } from 'react';
import { sendFeedback } from '../../service/apiService';
import { FeedbackRequest } from '../../types';
import { createPortal } from 'react-dom';
import { useUser } from '../../context/UserContext';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialSubject?: string;
  initialDescription?: string;
  hiddenMetadata?: string;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  initialSubject,
  initialDescription,
  hiddenMetadata
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { userDisplayName, userId } = useUser();
  const [formData, setFormData] = useState({
    name: userDisplayName || userId || '',
    message: ''
  });

  // Effect to set initial values when modal opens or props change
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        name: prev.name || userDisplayName || userId || '',
        message: initialDescription || prev.message
      }));
    }
  }, [isOpen, initialDescription, userDisplayName, userId]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    message?: string;
    files?: string;
  }>({});
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const MAX_FILES = 5;
  const ALLOWED_TYPES = [
    'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/bmp', 'image/webp',
    'application/pdf', 'text/plain', 'text/csv',
    'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/json', 'application/xml', 'application/zip', 'application/x-7z-compressed',
    'video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { name?: string; message?: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const filesArray = Array.from(files);
    const validationResult = validateFiles([...selectedFiles, ...filesArray]);

    if (!validationResult.valid) {
      setErrors(prev => ({ ...prev, files: validationResult.error }));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setSelectedFiles(prev => [...prev, ...filesArray]);
    setErrors(prev => ({ ...prev, files: undefined }));

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateFiles = (files: File[]): { valid: boolean; error?: string } => {
    if (files.length > MAX_FILES) {
      return {
        valid: false,
        error: `Maximum ${MAX_FILES} files allowed. You've selected ${files.length} files.`
      };
    }

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        return {
          valid: false,
          error: `File "${file.name}" is too large (${sizeMB}MB). Maximum size is 5MB per file.`
        };
      }

      if (!ALLOWED_TYPES.includes(file.type)) {
        return {
          valid: false,
          error: `File type not allowed for "${file.name}".`
        };
      }
    }

    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    const maxTotalSize = 10 * 1024 * 1024;
    if (totalSize > maxTotalSize) {
      const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(1);
      return {
        valid: false,
        error: `Total file size (${totalSizeMB}MB) exceeds 10MB limit.`
      };
    }

    return { valid: true };
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setErrors(prev => ({ ...prev, files: undefined }));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileType: string): string => {
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    if (fileType.includes('video')) return '🎥';
    if (fileType.includes('zip') || fileType.includes('7z')) return '📦';
    if (fileType.includes('json') || fileType.includes('xml')) return '⚙️';
    return '📎';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // NEW: Validate files if any selected
    if (selectedFiles.length > 0) {
      const fileValidation = validateFiles(selectedFiles);
      if (!fileValidation.valid) {
        setErrors(prev => ({ ...prev, files: fileValidation.error }));
        return;
      }
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      const finalMessage = hiddenMetadata
        ? `${formData.message.trim()}\n\n---\nSystem Context:\n${hiddenMetadata}`
        : formData.message.trim();

      // Prepare the full feedback request with auto-filled values
      const feedbackRequest: FeedbackRequest = {
        name: formData.name.trim(),
        from_email: 'kyva.inc.ai@gmail.com',
        to_email: 'info@kyva.ai',
        subject: initialSubject || 'Issue Report - Kyva Platform',
        message: finalMessage
      };

      // Pass files to sendFeedback
      const response = await sendFeedback(
        feedbackRequest,
        selectedFiles.length > 0 ? selectedFiles : undefined
      );

      if (response.success) {
        // Include attachment info in success message
        const attachmentInfo = response.data?.attachments_count
          ? ` with ${response.data.attachments_count} attachment(s)`
          : '';

        setSubmitStatus({
          type: 'success',
          message: response.message || `Thank you! Your message${attachmentInfo} has been sent successfully.`
        });

        // Reset form
        setFormData({
          name: '',
          message: ''
        }); setSelectedFiles([]);  // Clear files

        setTimeout(() => {
          onClose();
          setSubmitStatus({ type: null, message: '' });
        }, 3000);
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to send message. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      message: ''
    });
    setSelectedFiles([]);
    setErrors({});
    setSubmitStatus({ type: null, message: '' });
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999999
      }}
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="bg-surface-overlay text-primary rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto ring-1 ring-black/5 dark:ring-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Report Issue</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        {submitStatus.type && (
          <div className={`mb-4 p-3 rounded-md ${submitStatus.type === 'success'
            ? 'bg-green-50 border border-green-200 text-green-800'
            : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
            <div className="flex items-center">
              <span className="mr-2">
                {submitStatus.type === 'success' ? '✅' : '❌'}
              </span>
              {submitStatus.message}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 ${errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder="Your full name"
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          <div className="bg-gray-50 p-3 rounded-md text-sm text-gray-600">
            <p><strong>Subject:</strong> {initialSubject || 'Issue Report - Kyva Platform'}</p>
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
              Describe the Issue *
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={6}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 ${errors.message ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder="Please describe the issue you encountered in detail..."
            />
            {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Attachments (Optional)
            </label>

            {/* Hidden File Input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              accept=".png,.jpg,.jpeg,.gif,.bmp,.webp,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,.json,.xml,.zip,.7z,.mp4,.mov,.avi,.webm"
            />

            {/* Attach Files Button */}
            <div className="flex items-center space-x-2 mb-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={selectedFiles.length >= MAX_FILES}
                className={`px-4 py-2 text-sm border rounded-md flex items-center space-x-2 ${selectedFiles.length >= MAX_FILES
                    ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-violet-500'
                  }`}
              >
                <span>📎</span>
                <span>Attach Files</span>
              </button>
              <span className="text-xs text-gray-500">
                {selectedFiles.length}/{MAX_FILES} files • Max5MB each
              </span>
            </div>

            {/* File Error Message */}
            {errors.files && (
              <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
                {errors.files}
              </div>
            )}

            {/* Selected Files List */}
            {selectedFiles.length > 0 && (
              <div className="space-y-2 mb-3">
                {selectedFiles.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded-md"
                  >
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                      <span className="text-lg">{getFileIcon(file.type)}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(index)}
                      className="ml-2 text-red-500 hover:text-red-700 text-xl"
                      title="Remove file"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Supported File Types Info */}
            <p className="text-xs text-gray-500">
              Supported: Images, PDF, Word, Excel, Text files, Videos, Archives (ZIP)
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-4 py-2 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 ${isSubmitting
                ? 'bg-blue-400 cursor-not-allowed'
                : 'bg-violet-600 hover:bg-violet-700'
                }`}
            >
              {isSubmitting ? 'Sending...' : 'Send'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default FeedbackModal;